/**
 * Consent management for Bay Area Siding Company (CA privacy hardening).
 *
 * No ad/analytics trackers (GTM/GA4, Google Ads tags) load
 * until the visitor clicks Accept on the consent banner. Consent state is a
 * first-party cookie. Global Privacy Control (GPC) is honored: GPC visitors
 * see no banner and load no trackers.
 *
 * First-party attribution (landingURL / session_source in localStorage and
 * hidden form fields) is intentionally NOT consent-gated — data a customer
 * submits directly to us is first-party data, not third-party tracking.
 */

const CONSENT_COOKIE = 'basc_consent';
const DISMISSED_KEY = 'basc_consent_dismissed';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 12 months

const GTM_ID = 'GTM-WG5T3D2';

// Match the old gatsby-plugin-google-tagmanager behavior:
// includeInDevelopment was false, so trackers load in production builds only.
const TRACKING_ENABLED = process.env.NODE_ENV === 'production';

let trackersLoaded = false;
const listeners = [];

const isBrowser = () => typeof window !== 'undefined';

export const hasGPC = () =>
	isBrowser() && navigator.globalPrivacyControl === true;

export const hasConsent = () => {
	if (!isBrowser()) return false;
	return document.cookie
		.split(';')
		.some(c => c.trim().startsWith(`${CONSENT_COOKIE}=granted`));
};

export const isDismissedThisSession = () => {
	if (!isBrowser()) return false;
	try {
		return window.sessionStorage.getItem(DISMISSED_KEY) === '1';
	} catch (e) {
		return false;
	}
};

export const dismissForSession = () => {
	if (!isBrowser()) return;
	try {
		window.sessionStorage.setItem(DISMISSED_KEY, '1');
	} catch (e) {
		// sessionStorage unavailable — banner will just reappear
	}
	notify();
};

export const onConsentChange = fn => {
	listeners.push(fn);
	return () => {
		const i = listeners.indexOf(fn);
		if (i > -1) listeners.splice(i, 1);
	};
};

const notify = () => {
	listeners.forEach(fn => {
		try {
			fn();
		} catch (e) {
			// ignore listener errors
		}
	});
};

const gtag = function () {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push(arguments);
};

const injectGTM = () => {
	// Replicates gatsby-plugin-google-tagmanager's snippet + defaultDataLayer.
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({ platform: 'gatsby' });
	window.dataLayer.push({
		'gtm.start': new Date().getTime(),
		event: 'gtm.js'
	});
	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
	document.head.appendChild(script);
};

/**
 * Load the full tracker stack. Only ever called after consent exists
 * (Accept click, or returning visitor with the consent cookie).
 */
export const loadTrackers = () => {
	if (!isBrowser() || trackersLoaded || !TRACKING_ENABLED) return;
	if (hasGPC() || !hasConsent()) return;
	trackersLoaded = true;

	// Google Consent Mode v2: grant BEFORE GTM is injected.
	gtag('consent', 'update', {
		ad_storage: 'granted',
		analytics_storage: 'granted',
		ad_user_data: 'granted',
		ad_personalization: 'granted'
	});

	injectGTM();
};

/**
 * Accept click: set the first-party consent cookie, then load trackers.
 */
export const grantConsent = () => {
	if (!isBrowser()) return;
	document.cookie = `${CONSENT_COOKIE}=granted; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax; Secure`;
	notify();
	loadTrackers();
};

/**
 * Called once after hydration (onInitialClientRender): returning visitors
 * who already accepted get trackers on first render.
 */
export const initConsent = () => {
	if (!isBrowser() || hasGPC()) return;
	if (hasConsent()) loadTrackers();
};
